import { useState, useEffect } from "react";
import { Button, Card, Label, Select, TextInput, Checkbox, Badge, Alert } from "flowbite-react";
import { HiPlus, HiX, HiTrash, HiInformationCircle } from "react-icons/hi";

type FilterRule = {
  id: string;
  field: string;
  operator: string;
  value: any;
  entityType: "COMPANY" | "CONTACT" | "DEAL";
};

type FilterGroup = {
  id: string;
  logic: "AND" | "OR";
  rules: FilterRule[];
};

type SegmentFilterRules = {
  groups: FilterGroup[];
  logic: "AND" | "OR"; // Logic between groups
};

const ENTITY_FIELDS = {
  COMPANY: [
    { value: "name", label: "Company Name", type: "string", description: "Filter by company name" },
    { value: "sector", label: "Sector", type: "string", description: "Filter by industry sector" },
    { value: "size", label: "Company Size", type: "string", description: "Filter by company size" },
    { value: "city", label: "City", type: "string", description: "Filter by company location" },
    { value: "country", label: "Country", type: "string", description: "Filter by country" },
    { value: "status", label: "Status", type: "enum", options: ["ACTIVE", "INACTIVE"], description: "Filter by company status" },
    { value: "accountManagerId", label: "Account Manager", type: "number", description: "Filter by assigned account manager" },
  ],
  CONTACT: [
    { value: "firstName", label: "First Name", type: "string", description: "Filter by contact's first name" },
    { value: "lastName", label: "Last Name", type: "string", description: "Filter by contact's last name" },
    { value: "email", label: "Email", type: "string", description: "Filter by email address" },
    { value: "title", label: "Job Title", type: "string", description: "Filter by job title/position" },
    { value: "status", label: "Contact Status", type: "enum", options: ["NEW", "CONTACTED", "INVALID", "FOLLOW_UP"], description: "Filter by contact status" },
    { value: "hasNewsletter", label: "Subscribed to Newsletter", type: "boolean", description: "Filter by newsletter subscription" },
    { value: "companyId", label: "Belongs to Company", type: "number", description: "Filter contacts from specific company" },
    { value: "accountManagerId", label: "Account Manager", type: "number", description: "Filter by assigned account manager" },
  ],
  DEAL: [
    { value: "dealName", label: "Deal Name", type: "string", description: "Filter by deal name" },
    { value: "stage", label: "Deal Stage", type: "string", description: "Filter by deal stage (e.g., Opportunity, Proposal)" },
    { value: "amount", label: "Deal Amount", type: "number", description: "Filter by deal value" },
    { value: "companyId", label: "Company", type: "number", description: "Filter deals from specific company" },
    { value: "expectedClosingDate", label: "Expected Closing Date", type: "date", description: "Filter by expected closing date" },
  ],
};

const OPERATORS = {
  string: [
    { value: "equals", label: "Equals", description: "Exact match" },
    { value: "contains", label: "Contains", description: "Contains text" },
    { value: "startsWith", label: "Starts With", description: "Begins with text" },
    { value: "endsWith", label: "Ends With", description: "Ends with text" },
    { value: "notEquals", label: "Not Equals", description: "Does not equal" },
  ],
  number: [
    { value: "equals", label: "Equals", description: "Equal to" },
    { value: "greaterThan", label: "Greater Than", description: "Greater than value" },
    { value: "lessThan", label: "Less Than", description: "Less than value" },
    { value: "between", label: "Between", description: "Between two values" },
    { value: "notEquals", label: "Not Equals", description: "Not equal to" },
  ],
  boolean: [
    { value: "equals", label: "Is", description: "Is true or false" },
  ],
  enum: [
    { value: "equals", label: "Equals", description: "Equal to" },
    { value: "notEquals", label: "Not Equals", description: "Not equal to" },
    { value: "in", label: "In", description: "One of multiple values" },
  ],
  date: [
    { value: "equals", label: "Equals", description: "On this date" },
    { value: "after", label: "After", description: "After this date" },
    { value: "before", label: "Before", description: "Before this date" },
    { value: "between", label: "Between", description: "Between two dates" },
  ],
  array: [
    { value: "contains", label: "Contains", description: "Contains value" },
    { value: "notContains", label: "Not Contains", description: "Does not contain" },
  ],
};

interface SegmentBuilderProps {
  filterRules: SegmentFilterRules;
  onChange: (rules: SegmentFilterRules) => void;
  companies?: any[];
  users?: any[];
}

export const SegmentBuilder: React.FC<SegmentBuilderProps> = ({
  filterRules,
  onChange,
  companies = [],
  users = [],
}) => {
  const [rules, setRules] = useState<SegmentFilterRules>(
    filterRules || { groups: [], logic: "AND" }
  );

  useEffect(() => {
    setRules(filterRules || { groups: [], logic: "AND" });
  }, [filterRules]);

  const updateRules = (newRules: SegmentFilterRules) => {
    setRules(newRules);
    onChange(newRules);
  };

  const addGroup = () => {
    const newGroup: FilterGroup = {
      id: `group-${Date.now()}`,
      logic: "AND",
      rules: [
        {
          id: `rule-${Date.now()}`,
          field: "",
          operator: "",
          value: "",
          entityType: "CONTACT",
        },
      ],
    };
    updateRules({
      ...rules,
      groups: [...rules.groups, newGroup],
    });
  };

  const removeGroup = (groupId: string) => {
    updateRules({
      ...rules,
      groups: rules.groups.filter((g) => g.id !== groupId),
    });
  };

  const addRule = (groupId: string) => {
    const newRule: FilterRule = {
      id: `rule-${Date.now()}`,
      field: "",
      operator: "",
      value: "",
      entityType: "CONTACT",
    };
    updateRules({
      ...rules,
      groups: rules.groups.map((g) =>
        g.id === groupId ? { ...g, rules: [...g.rules, newRule] } : g
      ),
    });
  };

  const removeRule = (groupId: string, ruleId: string) => {
    updateRules({
      ...rules,
      groups: rules.groups.map((g) =>
        g.id === groupId ? { ...g, rules: g.rules.filter((r) => r.id !== ruleId) } : g
      ),
    });
  };

  const updateRule = (groupId: string, ruleId: string, updates: Partial<FilterRule>) => {
    updateRules({
      ...rules,
      groups: rules.groups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              rules: g.rules.map((r) => (r.id === ruleId ? { ...r, ...updates } : r)),
            }
          : g
      ),
    });
  };

  const getFieldType = (entityType: string, field: string): string => {
    const fieldDef = ENTITY_FIELDS[entityType as keyof typeof ENTITY_FIELDS]?.find(
      (f) => f.value === field
    );
    return fieldDef?.type || "string";
  };

  const getFieldOptions = (entityType: string, field: string): any[] => {
    const fieldDef = ENTITY_FIELDS[entityType as keyof typeof ENTITY_FIELDS]?.find(
      (f) => f.value === field
    );
    return fieldDef?.options || [];
  };

  const getFieldDescription = (entityType: string, field: string): string => {
    const fieldDef = ENTITY_FIELDS[entityType as keyof typeof ENTITY_FIELDS]?.find(
      (f) => f.value === field
    );
    return fieldDef?.description || "";
  };

  const renderRuleValue = (rule: FilterRule) => {
    const fieldType = getFieldType(rule.entityType, rule.field);
    const fieldOptions = getFieldOptions(rule.entityType, rule.field);

    if (fieldType === "boolean") {
      return (
        <Select
          value={rule.value === true ? "true" : rule.value === false ? "false" : ""}
          onChange={(e) => {
            const group = rules.groups.find(g => g.rules.some(r => r.id === rule.id));
            if (group) updateRule(group.id, rule.id, { value: e.target.value === "true" });
          }}
          className="flex-1"
        >
          <option value="">Select</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Select>
      );
    }

    if (fieldType === "enum" && fieldOptions.length > 0) {
      return (
        <Select
          value={rule.value || ""}
          onChange={(e) => {
            const group = rules.groups.find(g => g.rules.some(r => r.id === rule.id));
            if (group) updateRule(group.id, rule.id, { value: e.target.value });
          }}
          className="flex-1"
        >
          <option value="">Select value</option>
          {fieldOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Select>
      );
    }

    if (rule.field === "companyId") {
      return (
        <Select
          value={rule.value || ""}
          onChange={(e) => {
            const group = rules.groups.find(g => g.rules.some(r => r.id === rule.id));
            if (group) updateRule(group.id, rule.id, { value: parseInt(e.target.value) || null });
          }}
          className="flex-1"
        >
          <option value="">Select Company</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </Select>
      );
    }

    if (rule.field === "accountManagerId") {
      return (
        <Select
          value={rule.value || ""}
          onChange={(e) => {
            const group = rules.groups.find(g => g.rules.some(r => r.id === rule.id));
            if (group) updateRule(group.id, rule.id, { value: parseInt(e.target.value) || null });
          }}
          className="flex-1"
        >
          <option value="">Select User</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.firstName} {user.lastName}
            </option>
          ))}
        </Select>
      );
    }

    if (fieldType === "number" && rule.operator === "between") {
      return (
        <div className="flex gap-2 flex-1">
          <TextInput
            type="number"
            placeholder="Min"
            value={rule.value?.min || ""}
            onChange={(e) => {
              const group = rules.groups.find(g => g.rules.some(r => r.id === rule.id));
              if (group) updateRule(group.id, rule.id, {
                value: { ...rule.value, min: parseFloat(e.target.value) || 0 },
              });
            }}
            className="flex-1"
          />
          <TextInput
            type="number"
            placeholder="Max"
            value={rule.value?.max || ""}
            onChange={(e) => {
              const group = rules.groups.find(g => g.rules.some(r => r.id === rule.id));
              if (group) updateRule(group.id, rule.id, {
                value: { ...rule.value, max: parseFloat(e.target.value) || 0 },
              });
            }}
            className="flex-1"
          />
        </div>
      );
    }

    if (fieldType === "date" && rule.operator === "between") {
      return (
        <div className="flex gap-2 flex-1">
          <TextInput
            type="date"
            value={rule.value?.start || ""}
            onChange={(e) => {
              const group = rules.groups.find(g => g.rules.some(r => r.id === rule.id));
              if (group) updateRule(group.id, rule.id, {
                value: { ...rule.value, start: e.target.value },
              });
            }}
            className="flex-1"
          />
          <TextInput
            type="date"
            value={rule.value?.end || ""}
            onChange={(e) => {
              const group = rules.groups.find(g => g.rules.some(r => r.id === rule.id));
              if (group) updateRule(group.id, rule.id, {
                value: { ...rule.value, end: e.target.value },
              });
            }}
            className="flex-1"
          />
        </div>
      );
    }

    return (
      <TextInput
        type={fieldType === "number" ? "number" : fieldType === "date" ? "date" : "text"}
        value={rule.value || ""}
        onChange={(e) => {
          const group = rules.groups.find(g => g.rules.some(r => r.id === rule.id));
          if (group) updateRule(group.id, rule.id, { value: e.target.value });
        }}
        placeholder="Enter value"
        className="flex-1"
      />
    );
  };

  const hasValidRules = rules.groups.some(g => g.rules.some(r => r.field && r.operator && r.value !== "" && r.value !== null));

  return (
    <div className="space-y-3">
      {/* Compact Help Section */}
      <Alert color="info" className="py-2">
        <HiInformationCircle className="mr-2" />
        <span className="text-sm">
          Filter contacts by Company, Contact, or Deal fields. Use AND/OR logic to combine filters.
        </span>
      </Alert>

      {/* Logic Selector */}
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium mb-0">Match:</Label>
          <Select
            value={rules.logic}
            onChange={(e) => updateRules({ ...rules, logic: e.target.value as "AND" | "OR" })}
            className="w-32 text-sm"
          >
            <option value="AND">All Groups</option>
            <option value="OR">Any Group</option>
          </Select>
        </div>
        <Button size="sm" color="primary" onClick={addGroup}>
          <HiPlus className="mr-1" />
          Add Group
        </Button>
      </div>

      {/* Filter Groups */}
      {rules.groups.length === 0 ? (
        <Card className="text-center py-6 border-2 border-dashed border-gray-300 dark:border-gray-600">
          <HiInformationCircle className="mx-auto text-gray-400 text-3xl mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">No filters added yet</p>
          <Button size="sm" color="primary" onClick={addGroup}>
            <HiPlus className="mr-1" />
            Add Filter Group
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {rules.groups.map((group, groupIndex) => (
            <Card key={group.id} className="border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <Badge color="info" className="text-xs">
                    Group {groupIndex + 1}
                  </Badge>
                  <Select
                    value={group.logic}
                    onChange={(e) =>
                      updateRules({
                        ...rules,
                        groups: rules.groups.map((g) =>
                          g.id === group.id ? { ...g, logic: e.target.value as "AND" | "OR" } : g
                        ),
                      })
                    }
                    className="w-20 text-sm"
                  >
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </Select>
                </div>
                <Button size="xs" color="failure" onClick={() => removeGroup(group.id)}>
                  <HiTrash />
                </Button>
              </div>

              <div className="space-y-2">
                {group.rules.length === 0 ? (
                  <div className="text-center py-3 text-gray-500 dark:text-gray-400">
                    <Button size="sm" color="gray" onClick={() => addRule(group.id)}>
                      <HiPlus className="mr-1" />
                      Add Rule
                    </Button>
                  </div>
                ) : (
                  group.rules.map((rule) => (
                    <div key={rule.id} className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                      <div className="flex-1 grid grid-cols-12 gap-2 items-start">
                        {/* Entity Type */}
                        <div className="col-span-2">
                          <Select
                            value={rule.entityType}
                            onChange={(e) =>
                              updateRule(group.id, rule.id, {
                                entityType: e.target.value as "COMPANY" | "CONTACT" | "DEAL",
                                field: "",
                                operator: "",
                                value: "",
                              })
                            }
                            className="w-full text-sm"
                          >
                            <option value="CONTACT">Contact</option>
                            <option value="COMPANY">Company</option>
                            <option value="DEAL">Deal</option>
                          </Select>
                        </div>

                        {/* Field */}
                        <div className="col-span-3">
                          <Select
                            value={rule.field}
                            onChange={(e) =>
                              updateRule(group.id, rule.id, {
                                field: e.target.value,
                                operator: "",
                                value: "",
                              })
                            }
                            disabled={!rule.entityType}
                            className="w-full text-sm"
                          >
                            <option value="">Field</option>
                            {ENTITY_FIELDS[rule.entityType]?.map((field) => (
                              <option key={field.value} value={field.value}>
                                {field.label}
                              </option>
                            ))}
                          </Select>
                        </div>

                        {/* Operator */}
                        <div className="col-span-2">
                          <Select
                            value={rule.operator}
                            onChange={(e) =>
                              updateRule(group.id, rule.id, { operator: e.target.value, value: "" })
                            }
                            disabled={!rule.field}
                            className="w-full text-sm"
                          >
                            <option value="">Op</option>
                            {OPERATORS[getFieldType(rule.entityType, rule.field) as keyof typeof OPERATORS]?.map(
                              (op) => (
                                <option key={op.value} value={op.value}>
                                  {op.label}
                                </option>
                              )
                            )}
                          </Select>
                        </div>

                        {/* Value */}
                        <div className="col-span-4">
                          {renderRuleValue(rule)}
                        </div>

                        {/* Remove Button */}
                        <div className="col-span-1 flex items-end">
                          <Button
                            size="xs"
                            color="failure"
                            onClick={() => removeRule(group.id, rule.id)}
                            className="w-full"
                          >
                            <HiX />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}

                <Button size="sm" color="gray" onClick={() => addRule(group.id)} className="w-full">
                  <HiPlus className="mr-1" />
                  Add Rule
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Validation Message */}
      {rules.groups.length > 0 && !hasValidRules && (
        <Alert color="warning" className="mt-4">
          <HiInformationCircle className="mr-2" />
          <span>Please complete at least one filter rule to create a valid segment.</span>
        </Alert>
      )}
    </div>
  );
};
